import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import UFO from "./UFO";
import Zombie from "./Zombie";
import Beam from "./Beam";
import MapScene, { generateObstacles, isObstacleAt } from "./MapScene";
import HUD from "./HUD";
import PowersHUD from "./PowersHUD";
import Joystick from "./Joystick";
import BeamButton from "./BeamButton";
import { ZOMBIE_TYPES } from "./levels";
import RewardModal from "./RewardModal";
import GhostUFO from "./GhostUFO";
import { fetchGhosts, postGhost } from "./ghostService";
import { useAuth } from "../contexts/AuthContext";
import GameSim from "./GameSim";
import { computeTalentBonuses } from "./talents";
import { useUpgradeChooser } from "./useUpgradeChooser";
import { useRevives } from "./useRevives";
import { loadQuality, getQualityConfig } from "./quality";
import {
  LaserShots,
  Companions,
  LightningStrikes,
  Wheels,
  Balls,
  Dogs,
  Stars,
} from "./sceneEffects";
import {
  playBeamStart,
  stopBeam,
  playCollect,
  playCoin,
  playWin,
  playLose,
  playClick,
  playLaser,
  playLightning,
  playWheelHit,
  playBallHit,
  playDogBark,
} from "./sound";

const UFO_HEIGHT = 10;
const BASE_SPEED = 14; // units per second
const BASE_BEAM_RADIUS = 3.0;
const BASE_BEAM_STRENGTH = 1.2; // pull progress per second
const BEAM_MAX_HOLD = 10; // seconds the beam can be held continuously
const UI_FLUSH_HZ = 5; // batch UI re-renders to 5/sec (timer, score, coins, popups)

function createZombies(level, mapSize) {
  const zombies = [];
  const half = mapSize / 2 - 5;
  let id = 0;
  const place = (type, count) => {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 8 + Math.random() * (half - 8);
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      zombies.push({
        id: id++,
        type,
        typeData: ZOMBIE_TYPES[type],
        position: [x, 0, z],
        rotation: Math.random() * Math.PI * 2,
        wanderTimer: Math.random() * 2,
        wanderDir: Math.random() * Math.PI * 2,
        beingPulled: false,
        pullProgress: 0,
        removed: false,
      });
    }
  };
  place("regular", level.zombies.regular || 0);
  place("fast", level.zombies.fast || 0);
  place("big", level.zombies.big || 0);
  place("glowing", level.zombies.glowing || 0);
  return zombies;
}

// Camera rig follows the UFO with smoothing. Uses useFrame so it runs at the
// display refresh rate. No React state is touched here.
function CameraRig({ ufoStateRef }) {
  const { camera } = useThree();
  const target = useRef(new THREE.Vector3());
  useFrame((_, delta) => {
    const p = ufoStateRef.current;
    if (!p) return;
    // Frame-rate-independent lerp factor.
    const k = 1 - Math.pow(0.001, Math.min(delta, 0.05));
    const desiredX = p.x;
    const desiredY = UFO_HEIGHT + 20;
    const desiredZ = p.z + 22;
    camera.position.x += (desiredX - camera.position.x) * k;
    camera.position.y += (desiredY - camera.position.y) * k;
    camera.position.z += (desiredZ - camera.position.z) * k;
    target.current.set(p.x, p.y - 2, p.z);
    camera.lookAt(target.current);
  });
  return null;
}

// Simulation runs entirely in useFrame against delta time, mutating refs.
// No React state is set per frame — UI values are buffered in refs and
// flushed by a parent setInterval at low frequency.

function SceneInner({ level, obstacles, ufoStateRef, beamStateRef, zombies, beamActive, beamRadius, skin, beamColor, beamInnerColor, quality }) {
  // We read the UFO state ref directly in the JSX so that React Three Fiber
  // doesn't have to diff a new <UFO> position prop on every re-render of
  // the parent. Sync happens via a small per-frame ref-driven hook below.
  // Phase 6 — read per-world ambient + hemisphere settings.
  const ambIntensity = level.ambientIntensity || 0.85;
  const ambColor = level.ambientColor || "#b8a8e6";
  const shadowsOn = quality?.shadows !== false;
  const shadowMapSize = shadowsOn ? 1024 : 256;
  return (
    <>
      <CameraRig ufoStateRef={ufoStateRef} />
      <ambientLight intensity={ambIntensity} color={ambColor} />
      <hemisphereLight args={[ambColor, level.fogColor || "#1a0d2e", 0.65]} />
      <directionalLight
        position={[20, 30, 10]}
        intensity={1.1}
        color="#e8dfff"
        castShadow={shadowsOn}
        shadow-mapSize-width={shadowMapSize}
        shadow-mapSize-height={shadowMapSize}
      />
      <mesh position={[-30, 35, -50]}>
        <sphereGeometry args={[4, 16, 16]} />
        <meshStandardMaterial
          color="#fff8d6"
          emissive="#fff8d6"
          emissiveIntensity={1}
          toneMapped={false}
        />
      </mesh>

      <MapScene level={level} obstacles={obstacles} quality={quality} />
      <UFOFollow ufoStateRef={ufoStateRef} skin={skin} />
      <BeamFollow ufoStateRef={ufoStateRef} active={beamActive} radius={beamRadius} beamColor={beamColor} beamInnerColor={beamInnerColor} />
      {zombies.map((z) => (z.removed ? null : <Zombie key={z.id} zombie={z} />))}
    </>
  );
}

const Scene = React.memo(SceneInner);

// Wrapper around UFO that syncs its transform from a ref every frame
// without React re-renders.
function UFOFollow({ ufoStateRef, skin }) {
  const groupRef = useRef();
  useFrame(() => {
    const p = ufoStateRef.current;
    const g = groupRef.current;
    if (!p || !g) return;
    g.position.set(p.x, p.y, p.z);
    g.rotation.set(p.tiltX, 0, p.tiltZ);
  });
  return (
    <group ref={groupRef}>
      <UFO position={[0, 0, 0]} rotation={[0, 0, 0]} skin={skin} />
    </group>
  );
}

function BeamFollow({ ufoStateRef, active, radius, beamColor, beamInnerColor }) {
  const groupRef = useRef();
  useFrame(() => {
    const p = ufoStateRef.current;
    const g = groupRef.current;
    if (!p || !g) return;
    g.position.set(p.x, p.y - 0.3, p.z);
  });
  return (
    <group ref={groupRef}>
      <Beam position={[0, 0, 0]} active={active} radius={radius} height={UFO_HEIGHT} color={beamColor} innerColor={beamInnerColor} />
    </group>
  );
}


export default function Game({ level, upgrades, runPowers, setRunPowers, revivesRef, initRevives, isFreshRun, onWin, onLose, onQuit }) {
  const { user } = useAuth();
  const isVip = !!(user && user.is_vip);
  const isFounder = !!(user && user.founder);
  const [paused, setPaused] = useState(false);
  // UI state — only updated by the low-frequency flusher (5Hz).
  const [zombiesCollected, setZombiesCollected] = useState(0);
  // Pre-compute talent bonuses ONCE; talents.js owns all caps + per-level math.
  const talentBonuses = useMemo(
    () => computeTalentBonuses(user?.talent_tree),
    [user?.talent_tree]
  );
  // Initial state — apply "Bonus Starting Coins" talent (Phase 4) to give
  // the player a head-start coin pile that gets added to the run total.
  const startCoinsBonusRef = useRef(0);
  const [coinsEarned, setCoinsEarned] = useState(0);
  useEffect(() => {
    // Soft VIP / Founder perk (Feb 2026): flat bonus starting coins so the
    // tier feels valuable. Only applied on FRESH runs (not on each wave
    // remount) — Founder takes priority over VIP.
    const vipStartBonus = isFreshRun ? (isFounder ? 200 : (isVip ? 100 : 0)) : 0;
    const bonus = talentBonuses.startCoinsBonus + vipStartBonus;
    if (bonus > 0 && startCoinsBonusRef.current === 0) {
      startCoinsBonusRef.current = bonus;
      setCoinsEarned((c) => c + bonus);
    }
    // Init revives only on a FRESH run. Subsequent waves remount this
    // component (key changes) but isFreshRun=false signals "this is a
    // continuation" so revives + start-coins bonus are NOT re-applied.
    if (isFreshRun && initRevives) {
      initRevives(talentBonuses.extraRevives);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentBonuses.startCoinsBonus, isVip, isFounder, isFreshRun]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(level.time);
  const [popups, setPopups] = useState([]);
  const [beamActiveUI, setBeamActiveUI] = useState(false);
  const [bossActive, setBossActive] = useState(false);
  const [bossHp, setBossHp] = useState(0);
  const [beamTimeLeft, setBeamTimeLeft] = useState(BEAM_MAX_HOLD);
  const [beamLocked, setBeamLocked] = useState(false);
  // Forces a re-render of the zombies list when a zombie is added/removed.
  const [zombiesVersion, setZombiesVersion] = useState(0);
  // Touch mode = show the on-screen joystick + beam button and disable the
  // desktop "cursor follow" steering. We detect on mount AND on resize so
  // both phones and DevTools-emulated viewports get the right controls.
  const [touchMode, setTouchMode] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return (
        window.matchMedia("(pointer: coarse)").matches ||
        "ontouchstart" in window
      );
    } catch (_) {
      return false;
    }
  });
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setTouchMode(mq.matches || "ontouchstart" in window);
    if (mq.addEventListener) mq.addEventListener("change", update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  const obstacles = useMemo(() => generateObstacles(level), [level]);
  const runStartRef = useRef(performance.now());
  const bossesKilledRef = useRef(0);

  // Quality config — read once on mount. Switching quality mid-game is rare
  // and requires a fresh Canvas (key change), so we don't subscribe to
  // localStorage changes here.
  const qualityCfg = useMemo(() => getQualityConfig(loadQuality()), []);

  // Refs (mutated every frame — no React re-renders).
  const ufoStateRef = useRef({ x: 0, y: UFO_HEIGHT, z: 0, vx: 0, vz: 0, tiltX: 0, tiltZ: 0 });
  const beamStateRef = useRef({ active: false, charge: 1, holdTime: 0, locked: false });
  const beamUIRef = useRef(false);
  const zombiesRef = useRef(createZombies(level, level.mapSize));
  const bossStateRef = useRef({ spawned: false, collectedCount: 0 });
  const laserShotsRef = useRef([]);
  // Companion ships: small UFOs that hover beside the main UFO and fire
  // weaker beams. Positions are computed each frame from the main UFO ref.
  const companionsRef = useRef([
    { x: 0, y: UFO_HEIGHT, z: 0, ox: 4.0, oz: 3.0 },
    { x: 0, y: UFO_HEIGHT, z: 0, ox: -4.0, oz: 3.0 },
    { x: 0, y: UFO_HEIGHT, z: 0, ox: 0.0, oz: 4.5 },
  ]);
  // Sky lightning strikes — buffer of {id, x, z, y, born} for visuals.
  // Sky lightning visual buffer (managed entirely inside GameSim).
  const lightningStrikesRef = useRef([]);
  // Rolling Wheels — orbiting torus shapes that crush zombies on contact.
  // Each entry: { angle, radius, speed, size, x, z, dmgCd }
  const wheelsRef = useRef([]);
  // Bouncing Balls — physics-based balls that ricochet off map borders.
  // Each entry: { x, z, vx, vz, size, hitCd }
  const ballsRef = useRef([]);
  // Super Dogs — chase the nearest zombie and tackle it on contact.
  // Each entry: { x, z, vx, vz, size, speed, targetId, hitCd }
  const dogsRef = useRef([]);
  // Mirror of config used inside long-lived intervals/effects so values
  // stay fresh without re-subscribing the interval each frame.
  const configRef = useRef(null);
  // Ghost UFOs — paths of other players' recent runs, replayed on loop for
  // async multiplayer flavor.
  const [ghosts, setGhosts] = useState([]);
  // Sparse sampler of THIS run's UFO position, posted to the backend when
  // the player leaves the level so future runs can see their ghost.
  const ghostPathRef = useRef([]);
  const ghostStartRef = useRef(0);
  const inputRef = useRef({
    keys: {},
    beam: false,
    mouse: { x: 0, y: 0, active: false },
    joystick: { dx: 0, dz: 0, active: false },
  });
  const endedRef = useRef(false);
  const uiBufRef = useRef({
    coins: 0,
    score: 0,
    collected: 0,
    elapsedDt: 0,
    popups: [],
    sfx: [],
    popupNextId: 0,
  });

  // Ref-payload that the revive hook reads to construct the onLose payload
  // without re-subscribing the effect on every score/coin change. Updated
  // by the 5Hz flusher.
  const losePayloadRef = useRef({ score: 0, coinsEarned: 0, zombiesCollected: 0 });

  // Upgrade-derived stats — all upgrades are now in-run rewards. Memoized
  // so config object is stable across renders.
  // Read talent levels ONCE at config time (multipliers are pre-multiplied
  // into beamRadius/beamStrength/speedMult so useFrame stays branch-free).
  const config = useMemo(() => {
    // ---- permanent talent passives (Phase 4) — see /app/frontend/src/game/talents.js
    const {
      ufoTalentMult,
      beamRangeTalent,
      beamPullTalent,
      coinTalentMult,
      bossHpTalentMult,
    } = talentBonuses;

    // ---- in-run "Deaded Enemy Reward" powers (temporary)
    const beamSizeLvl = runPowers.beamSize || 0;
    const beamPullLvl = runPowers.beamPull || 0;
    const beamHoldLvl = runPowers.beamHold || 0;
    const ufoBoostLvl = runPowers.ufoBoost || 0;
    const freezeLvl = runPowers.freeze || 0;
    const coinBoostLvl = runPowers.coinBoost || 0;
    const tripleBeamLvl = runPowers.tripleBeam || 0;
    const laserLvl = runPowers.laser || 0;
    const magnetLvl = runPowers.magnet || 0;

    const speedMult = (1 + ufoBoostLvl * 0.15) * ufoTalentMult;
    const beamRadius =
      BASE_BEAM_RADIUS * (1 + beamSizeLvl * 0.25) * (1 + tripleBeamLvl * 0.20) * beamRangeTalent;
    const beamStrength = BASE_BEAM_STRENGTH * (1 + beamPullLvl * 0.30) * beamPullTalent;
    const freezeAmount = Math.min(0.85, freezeLvl * 0.12);
    const multMult = (1 + coinBoostLvl * 0.25) * coinTalentMult;
    const beamMaxHold = BEAM_MAX_HOLD + beamHoldLvl * 2;

    // Auto Laser: enabled at Lv 1+; rate grows past Lv 5 (cap fire rate so
    // it doesn't drown CPU).
    const laser = {
      enabled: laserLvl > 0,
      rate: Math.min(5.0, 0.5 + (laserLvl - 1) * 0.4),
      range: 8 + laserLvl * 1.6,
      progressPerHit: 0.55 + Math.max(0, laserLvl - 5) * 0.1,
    };
    // Tractor Magnet: strength keeps scaling; range capped to keep it sane.
    const magnet = {
      enabled: magnetLvl > 0,
      strength: magnetLvl * 0.4,
      range: magnetLvl > 0 ? Math.min(24, 9 + magnetLvl * 1.5) : 0,
    };
    const waveSpeedBoost = level.waveSpeedBoost || 1;

    // Companion ships — count caps at 3 (matches the 3 visual slots in
    // sceneEffects.Companions and the 3 init positions in companionsRef).
    // Higher levels keep scaling beam strength instead of adding ships.
    const compLvl = runPowers.companion || 0;
    const compCount = compLvl === 0 ? 0 : Math.min(3, 1 + Math.floor(compLvl / 2));
    const companion = {
      enabled: compLvl > 0,
      count: compCount,
      strengthMul: Math.min(0.9, 0.25 + compLvl * 0.07),
      radiusMul: 0.55,
    };
    // Sky lightning — interval shrinks each level, capped at 0.4s. Targets
    // and big-zombie bonus keep growing.
    const lLvl = runPowers.lightning || 0;
    const lightning = {
      enabled: lLvl > 0,
      interval: lLvl === 0 ? Infinity : Math.max(0.4, 5.0 * Math.pow(0.78, lLvl - 1)),
      targets: lLvl === 0 ? 0 : Math.min(6, 1 + Math.floor(lLvl / 2)),
      bigBonus: lLvl >= 4 ? 1.6 + (lLvl - 4) * 0.15 : 1,
      progress: 0.55 + Math.max(0, lLvl - 5) * 0.08,
      range: 16 + Math.max(0, lLvl - 5) * 1.5,
    };
    // Rolling wheels — count caps at 4, then speed/damage keep scaling.
    const wLvl = runPowers.wheel || 0;
    const wheel = {
      enabled: wLvl > 0,
      count: wLvl === 0 ? 0 : Math.min(4, 1 + Math.floor(wLvl / 2)),
      orbitRadius: 4.5 + Math.min(wLvl, 5) * 0.4,
      angularSpeed: 1.4 + wLvl * 0.32,
      size: 0.9 + Math.min(wLvl, 5) * 0.1,
      progress: 0.32 + wLvl * 0.07,
      contactCd: 0.4,
    };
    // Bouncing balls — same scaling pattern as wheels.
    const bLvl = runPowers.ball || 0;
    const ball = {
      enabled: bLvl > 0,
      count: bLvl === 0 ? 0 : Math.min(4, 1 + Math.floor(bLvl / 2)),
      speed: 12 + bLvl * 2.2,
      size: 0.8 + Math.min(bLvl, 5) * 0.1,
      progress: 0.6 + bLvl * 0.12,
      contactCd: 0.35,
    };
    // Super dogs — same scaling pattern.
    const dLvl = runPowers.dog || 0;
    const dog = {
      enabled: dLvl > 0,
      count: dLvl === 0 ? 0 : Math.min(4, 1 + Math.floor(dLvl / 2)),
      speed: 8 + dLvl * 1.6,
      size: 0.85 + Math.min(dLvl, 5) * 0.08,
      progress: 1.0,
      contactCd: Math.max(0.25, 0.6 - dLvl * 0.04),
    };
    return {
      speedMult,
      beamRadius,
      beamStrength,
      beamMaxHold,
      freezeAmount,
      multMult,
      touchMode,
      laser,
      magnet,
      waveSpeedBoost,
      companion,
      lightning,
      wheel,
      ball,
      dog,
      bossHpTalentMult,
    };
  }, [runPowers, touchMode, level.waveSpeedBoost, talentBonuses]);

  // ----- Phase 6 refactor: extracted hooks ----------------------------
  // Upgrade chooser, VIP reroll, Lucky Run consume, run-power-gain
  // accounting all live in useUpgradeChooser.
  const { chooserOptions, handleChoose, handleReroll } = useUpgradeChooser({
    level,
    runPowers,
    setRunPowers,
    zombiesCollected,
    isVip,
    endedRef,
    user,
    talentBonuses,
  });

  // Revive flow (extra_revive talent): restores +20s on timer-0, falls
  // through to onLose when revives run out.
  const { reviveFlash } = useRevives({
    endedRef,
    timeLeft,
    setTimeLeft,
    revivesRef,
    stopBeam,
    playLose,
    onLose,
    runStartRef,
    bossesKilledRef,
    payloadRef: losePayloadRef,
  });
  // --------------------------------------------------------------------

  // Keep a ref in sync with the latest config so long-lived effects (the
  // 5Hz flusher) can read current values without re-subscribing.
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Fetch ghost paths once per level. Best-effort — silent on failure.
  useEffect(() => {
    let cancelled = false;
    fetchGhosts(level.id, 3).then((list) => {
      if (!cancelled) setGhosts(list);
    });
    return () => {
      cancelled = true;
    };
  }, [level.id]);

  // Sample the player's UFO position every 250ms while playing. Skipped
  // while the chooser is open or the game is paused so the recording
  // reflects actual movement only.
  useEffect(() => {
    ghostPathRef.current = [];
    ghostStartRef.current = performance.now() / 1000;
    const id = setInterval(() => {
      if (paused || chooserOptions || endedRef.current) return;
      const u = ufoStateRef.current;
      const t = performance.now() / 1000 - ghostStartRef.current;
      const arr = ghostPathRef.current;
      // Cap to 320 samples (~80s). Older samples drop off the front.
      if (arr.length >= 320) arr.shift();
      arr.push({ t: +t.toFixed(2), x: +u.x.toFixed(2), z: +u.z.toFixed(2) });
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level.id, level.wave, paused, chooserOptions]);

  // Post this run's path when the Game component unmounts (player wins,
  // loses, or quits). Fire-and-forget — never blocks navigation.
  useEffect(() => {
    return () => {
      const path = ghostPathRef.current;
      if (!path || path.length < 4) return;
      const duration = path[path.length - 1].t - path[0].t;
      postGhost({
        levelId: level.id,
        wave: level.wave || 1,
        duration,
        path,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the lose-payload ref in sync with the live UI state so the revive
  // hook can read the latest values without re-subscribing on every flush.
  useEffect(() => {
    losePayloadRef.current = { score, coinsEarned, zombiesCollected };
  }, [score, coinsEarned, zombiesCollected]);

  // Low-frequency UI flusher: pushes buffered numbers into React state at 5Hz.
  useEffect(() => {
    const id = setInterval(() => {
      if (endedRef.current) return;
      const buf = uiBufRef.current;
      // Timer
      if (buf.elapsedDt > 0) {
        const dt = buf.elapsedDt;
        buf.elapsedDt = 0;
        setTimeLeft((t) => Math.max(0, t - dt));
      }
      // Score / coins / collected
      if (buf.score) {
        const v = buf.score;
        buf.score = 0;
        setScore((s) => s + v);
      }
      if (buf.coins) {
        const v = buf.coins;
        buf.coins = 0;
        setCoinsEarned((c) => c + v);
      }
      if (buf.collected) {
        const v = buf.collected;
        buf.collected = 0;
        setZombiesCollected((c) => c + v);
      }
      // Popups
      if (buf.popups.length) {
        const newPopups = buf.popups.splice(0);
        setPopups((p) => [...p, ...newPopups]);
        // Remove each after 0.9s
        newPopups.forEach((np) => {
          setTimeout(() => {
            setPopups((p) => p.filter((x) => x.id !== np.id));
          }, 900);
        });
      }
      // SFX (debounced — only play first of each kind per flush)
      if (buf.sfx.length) {
        const kinds = new Set(buf.sfx);
        buf.sfx.length = 0;
        if (kinds.has("collect")) playCollect();
        if (kinds.has("coin")) playCoin();
        if (kinds.has("laser")) playLaser();
        if (kinds.has("lightning")) playLightning();
      }
      // Boss HP refresh
      if (level.boss) {
        const boss = zombiesRef.current.find((z) => z.isBoss && !z.removed);
        if (boss) {
          setBossActive(true);
          setBossHp((1 - boss.pullProgress) * boss.typeData.hp);
        } else if (bossStateRef.current.spawned) {
          if (!bossStateRef.current.counted) {
            bossesKilledRef.current += 1;
            bossStateRef.current.counted = true;
          }
          setBossActive(false);
          setBossHp(0);
        }
      }
      // Beam time-left / locked state (kept in sync with simulation refs)
      const b = beamStateRef.current;
      const maxHold = (configRef.current && configRef.current.beamMaxHold) || BEAM_MAX_HOLD;
      const tl = Math.max(0, maxHold - b.holdTime);
      setBeamTimeLeft((prev) => (Math.abs(prev - tl) > 0.05 ? tl : prev));
      setBeamLocked((prev) => (prev !== b.locked ? b.locked : prev));
    }, 1000 / UI_FLUSH_HZ);
    return () => clearInterval(id);
  }, [level.boss]);

  // Keyboard listeners
  useEffect(() => {
    const down = (e) => {
      if (e.key === " ") {
        e.preventDefault();
        inputRef.current.keys[" "] = true;
      } else {
        inputRef.current.keys[e.key] = true;
      }
      if (e.key === "Escape") {
        setPaused((p) => !p);
      }
    };
    const up = (e) => {
      if (e.key === " ") {
        e.preventDefault();
        inputRef.current.keys[" "] = false;
      } else {
        inputRef.current.keys[e.key] = false;
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      stopBeam();
    };
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      if (touchMode) return; // touch input is handled by joystick + beam button
      inputRef.current.beam = true;
    },
    [touchMode]
  );
  const onPointerUp = useCallback(
    (e) => {
      if (touchMode) return;
      inputRef.current.beam = false;
    },
    [touchMode]
  );
  // Track cursor over the canvas to steer the UFO. Updates a ref only — no
  // React state, so this adds zero re-renders per move event.
  const onPointerMove = useCallback(
    (e) => {
      if (touchMode) return;
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
      const m = inputRef.current.mouse;
      m.x = x;
      m.y = y;
      m.active = true;
    },
    [touchMode]
  );
  const onPointerLeave = useCallback(() => {
    if (touchMode) return;
    inputRef.current.beam = false;
    inputRef.current.mouse.active = false;
  }, [touchMode]);

  // Win / Lose checks (triggered by UI state updates from the flusher).
  useEffect(() => {
    if (endedRef.current) return;
    if (zombiesCollected >= level.goal) {
      if (level.boss) {
        const bossLeft = zombiesRef.current.find((z) => z.isBoss && !z.removed);
        if (bossStateRef.current.spawned && !bossLeft) {
          endedRef.current = true;
          stopBeam();
          playWin();
          const playSeconds = Math.round((performance.now() - runStartRef.current) / 1000);
          setTimeout(() => onWin({
            score, coinsEarned, zombiesCollected,
            bossesDefeated: bossesKilledRef.current,
            playSeconds,
          }), 400);
        }
      } else {
        endedRef.current = true;
        stopBeam();
        playWin();
        const playSeconds = Math.round((performance.now() - runStartRef.current) / 1000);
        setTimeout(() => onWin({
          score, coinsEarned, zombiesCollected,
          bossesDefeated: bossesKilledRef.current,
          playSeconds,
        }), 400);
      }
    }
  }, [zombiesCollected, level, onWin, score, coinsEarned]);

  useEffect(() => {
    // Per-level cleanup of transient effect arrays — chooser/lucky-run reset
    // is handled inside useUpgradeChooser.
    wheelsRef.current = [];
    ballsRef.current = [];
    dogsRef.current = [];
  }, [level.id, level.wave, level.goal]);

  const handlePause = useCallback(() => {
    playClick();
    setPaused((p) => !p);
  }, []);

  // Build the zombie list only when version changes (add/remove). Position
  // mutations don't trigger re-renders — the Zombie component reads from
  // zombie.position each frame via its own useFrame. We slice the array so
  // the memoized Scene sees a new reference and reconciles the list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liveZombies = useMemo(() => zombiesRef.current.slice(), [zombiesVersion]);

  return (
    <div
      className="game-root"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      data-testid="game-canvas-wrap"
    >
      <Canvas
        shadows={qualityCfg.shadows}
        frameloop="always"
        dpr={qualityCfg.dpr}
        camera={{ fov: 62, position: [0, 30, 22] }}
        gl={{ antialias: qualityCfg.antialias, powerPreference: "high-performance" }}
        style={{ background: level.skyColor }}
      >
        <fog attach="fog" args={[level.fogColor, level.fogNear || 30, level.fogFar || 90]} />
        <color attach="background" args={[level.skyColor]} />
        <Stars />
        <GameSim
          level={level}
          obstacles={obstacles}
          config={config}
          paused={paused || !!chooserOptions}
          ufoStateRef={ufoStateRef}
          beamStateRef={beamStateRef}
          zombiesRef={zombiesRef}
          bossStateRef={bossStateRef}
          inputRef={inputRef}
          uiBufRef={uiBufRef}
          beamUIRef={beamUIRef}
          setBeamActiveUI={setBeamActiveUI}
          setZombiesVersion={setZombiesVersion}
          laserShotsRef={laserShotsRef}
          companionsRef={companionsRef}
          lightningStrikesRef={lightningStrikesRef}
          wheelsRef={wheelsRef}
          ballsRef={ballsRef}
          dogsRef={dogsRef}
        />
        <Scene
          level={level}
          obstacles={obstacles}
          ufoStateRef={ufoStateRef}
          beamStateRef={beamStateRef}
          zombies={liveZombies}
          beamActive={beamActiveUI}
          beamRadius={config.beamRadius}
          skin={user?.equipped_visuals?.ufo_skin?.visual}
          beamColor={user?.equipped_visuals?.beam_effect?.visual?.color}
          beamInnerColor={user?.equipped_visuals?.beam_effect?.visual?.emissive}
          quality={qualityCfg}
        />
        <LaserShots laserShotsRef={laserShotsRef} />
        <Companions companionsRef={companionsRef} count={config.companion.count} beamActive={beamActiveUI} beamRadius={config.beamRadius * config.companion.radiusMul} />
        <LightningStrikes strikesRef={lightningStrikesRef} />
        <Wheels wheelsRef={wheelsRef} count={config.wheel.count} />
        <Balls ballsRef={ballsRef} count={config.ball.count} />
        <Dogs dogsRef={dogsRef} count={config.dog.count} />
        {ghosts.map((g, i) => (
          <GhostUFO
            key={g.id}
            path={g.path}
            name={g.name}
            offset={i * 2.3}
            color={["#9ad8ff", "#ffb1e3", "#c6ff9a"][i % 3]}
          />
        ))}
      </Canvas>

      <HUD
        level={level}
        zombiesCollected={zombiesCollected}
        goal={level.goal}
        coinsEarned={coinsEarned}
        score={score}
        timeLeft={timeLeft}
        beamActive={beamActiveUI}
        beamCharge={beamStateRef.current.charge}
        beamTimeLeft={beamTimeLeft}
        beamMaxHold={config.beamMaxHold}
        beamLocked={beamLocked}
        bossActive={bossActive}
        bossHp={bossHp}
        onPause={handlePause}
        touchMode={touchMode}
      />

      <PowersHUD runPowers={runPowers} />

      {reviveFlash > 0 && (
        <div
          key={reviveFlash}
          className="revive-flash"
          data-testid="revive-flash"
        >
          <div className="revive-flash-title">REVIVED!</div>
          <div className="revive-flash-sub">+20s — {revivesRef?.current || 0} left</div>
        </div>
      )}

      {touchMode && (
        <>
          <Joystick inputRef={inputRef} />
          <BeamButton
            inputRef={inputRef}
            timeLeft={beamTimeLeft}
            maxHold={config.beamMaxHold}
            locked={beamLocked}
          />
        </>
      )}

      <RewardModal
        options={chooserOptions}
        runPowers={runPowers}
        onChoose={handleChoose}
        onReroll={isVip ? handleReroll : null}
      />

      <div className="popups">
        {popups.map((p) => (
          <div key={p.id} className="popup-item" style={{ color: p.color }}>
            <div>{p.text}</div>
            <div className="popup-sub">{p.sub}</div>
          </div>
        ))}
      </div>

      {paused && (
        <div className="pause-overlay" data-testid="pause-overlay">
          <div className="pause-card">
            <h2>Paused</h2>
            <button
              type="button"
              className="zc-btn primary"
              onClick={handlePause}
              data-testid="resume-button"
            >
              Resume
            </button>
            <button
              type="button"
              className="zc-btn ghost"
              onClick={() => {
                playClick();
                stopBeam();
                onQuit();
              }}
              data-testid="quit-button"
            >
              Quit to Menu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
